// EventDetailPage — детальная страница события с attendees, respond-actions
// и ссылкой на связанную сделку CRM (R21.2).
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Alert,
  AvatarGroup,
  Link as MuiLink,
} from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { AttendeeAvatar } from './components/AttendeeAvatar';
import { calendarApi, type AttendeeStatus } from '@/api/calendar';
import { useDealTitleOrUnavailable } from '@/hooks/useDealTitleOrUnavailable';
import { t } from '@/i18n';
import { formatEventType, formatAttendeeStatus } from '@/lib/format';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const RESPOND_OPTIONS: AttendeeStatus[] = ['ACCEPTED', 'TENTATIVE', 'DECLINED'];

/** Детальная страница события. */
export function EventDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const eventQuery = useQuery({
    queryKey: ['calendar-event', id],
    queryFn: () => calendarApi.get(id ?? ''),
    enabled: Boolean(id),
  });

  const attendeesQuery = useQuery({
    queryKey: ['calendar-event-attendees', id],
    queryFn: () => calendarApi.listAttendees(id ?? ''),
    enabled: Boolean(id),
  });

  // R21.2: название связанной сделки подгружаем только здесь (KISS: в гриде — только иконка).
  // Hook объявлен до early return (rules-of-hooks); enabled — по факту загрузки события.
  // FE-1 (REVIEW-20260907, ADR-2): FIX-C-канон (useDealTitleOrUnavailable) — 404
  // canceled-сделки не ретраится, UI показывает «Сделка удалена», а не вечную «Загрузка…».
  const dealId = eventQuery.data?.dealId ?? null;
  const dealResolutions = useDealTitleOrUnavailable(dealId ? [dealId] : []);
  const dealResolution = dealId ? dealResolutions.get(dealId) : undefined;

  const respondMutation = useMutation({
    mutationFn: (status: AttendeeStatus) =>
      calendarApi.respondAttendee(id ?? '', 'me', { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-event-attendees', id] });
    },
  });

  if (eventQuery.isLoading) return <LoadingSpinner label="Загрузка события…" />;
  if (eventQuery.isError || !eventQuery.data) {
    return (
      <EmptyState
        title="Событие не найдено"
        description={eventQuery.error instanceof Error ? eventQuery.error.message : 'Неизвестная ошибка'}
        actionLabel="К календарю"
        onAction={() => navigate('/calendar')}
      />
    );
  }
  const event = eventQuery.data;

  return (
    <Box>
      <PageHeader
        title={event.title}
        subtitle={`${format(new Date(event.startAt), 'd MMMM yyyy, HH:mm', { locale: ru })} — ${format(new Date(event.endAt), 'HH:mm', { locale: ru })}`}
      />
      {respondMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {respondMutation.error instanceof Error ? respondMutation.error.message : 'Не удалось отправить ответ'}
        </Alert>
      ) : null}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            {event.description ? (
              <Typography variant="body1">{event.description}</Typography>
            ) : null}
            {event.location ? (
              <Typography variant="body2" color="text.secondary">
                📍 {event.location}
              </Typography>
            ) : null}
            {event.dealId ? (
              // FE-1 (ADR-2): сделка недоступна (404/canceled) → «Сделка удалена»
              // некликабельно (канон FIX-C TaskDetailPage/TasksListPage).
              dealResolution?.unavailable ? (
                <Typography variant="body2" color="text.secondary">
                  Сделка:{' '}
                  <Typography component="span" variant="body2">
                    {t('calendar.dealDeleted')}
                  </Typography>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Сделка:{' '}
                  <MuiLink component={RouterLink} to={`/crm/deals/${event.dealId}`} variant="body2">
                    {dealResolution?.title ?? t('common.loading')}
                  </MuiLink>
                </Typography>
              )
            ) : null}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Тип
              </Typography>
              {/* R41 / U-16: подпись типа через i18n, не сырой enum. */}
              <Typography variant="body2">{formatEventType(event.type, t)}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Участники ({attendeesQuery.data?.length ?? 0})
      </Typography>
      {attendeesQuery.isLoading ? (
        <LoadingSpinner />
      ) : !attendeesQuery.data || attendeesQuery.data.length === 0 ? (
        <EmptyState title="Нет участников" description="Добавьте участников через API" />
      ) : (
        <Stack spacing={2}>
          <AvatarGroup max={10}>
            {attendeesQuery.data.map((a) => (
              <AttendeeAvatar key={a.id} attendee={a} size={40} />
            ))}
          </AvatarGroup>
          <Stack direction="row" spacing={1}>
            {RESPOND_OPTIONS.map((status) => (
              <Button
                key={status}
                variant="outlined"
                size="small"
                onClick={() => respondMutation.mutate(status)}
                disabled={respondMutation.isPending}
              >
                {/* R41 / U-16: подпись статуса ответа через i18n, не сырой enum. */}
                {formatAttendeeStatus(status, t)}
              </Button>
            ))}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
